// Lays all-day events out as continuous bars across a row of days (a week
// header, or one week of the month grid), so a multi-day event is one bar
// instead of a chip repeated in every day it touches.

import { addDays, startOfDay } from 'date-fns'
import type { CalendarEvent } from './typings'
import { compareEventsByStart } from './eventOrder'
import { daysCovered, eventInterval } from './eventSpan'

/** Rows a day shows before the last one becomes "+N more". */
export const MAX_ROWS = 3

export type BarSegment = {
  event: CalendarEvent
  /** Inclusive column indexes into the row's `days`. */
  startCol: number
  endCol: number
  lane: number
  continuesBefore: boolean
  continuesAfter: boolean
}

export type BarRowLayout = {
  segments: BarSegment[]
  /** Rows each day shows, counting the "+N more" row when it overflows. */
  rowsByDay: number[]
  /** Lanes reserved on each day, already capped to what the day can show. */
  slotsByDay: number[]
  /** Bars that did not fit on each day — they feed its "+N more". */
  hiddenByDay: number[]
}

/** An all-day event covering more than one calendar day. */
export function isMultiDayBar(event: CalendarEvent): boolean {
  if (!event.allDay) return false
  const { start, end } = eventInterval(event)
  return end > addDays(startOfDay(start), 1)
}

/** Rows a day shows given its bar lanes and the other items below them. */
export function visibleRows(laneCount: number, otherCount: number): number {
  return laneCount + otherCount > MAX_ROWS ? MAX_ROWS - 1 : MAX_ROWS
}

function packLanes(events: CalendarEvent[], days: Date[]): BarSegment[] {
  const rowEnd = addDays(days[days.length - 1], 1)
  const placed = events.flatMap((event) => {
    const cols = daysCovered(event, days)
    if (cols.length === 0) return []
    const { start, end } = eventInterval(event)
    return [
      {
        event,
        startCol: cols[0],
        endCol: cols[cols.length - 1],
        continuesBefore: start < days[0],
        continuesAfter: end > rowEnd,
      },
    ]
  })

  placed.sort(
    (a, b) =>
      a.startCol - b.startCol ||
      b.endCol - b.startCol - (a.endCol - a.startCol) ||
      compareEventsByStart(a.event, b.event),
  )

  const laneEnds: number[] = []
  return placed.map((item) => {
    let lane = laneEnds.findIndex((end) => end < item.startCol)
    if (lane === -1) lane = laneEnds.length
    laneEnds[lane] = item.endCol
    return { ...item, lane }
  })
}

/**
 * Packs `events` into non-overlapping lanes across `days`, then drops the bars
 * a day has no room for. `otherByDay` is how many non-bar rows each day also
 * has to fit; a bar is cut into runs around the days that hide it.
 */
export function layoutBarRow(
  events: CalendarEvent[],
  days: Date[],
  otherByDay: number[] = days.map(() => 0),
): BarRowLayout {
  const packed = packLanes(events, days)

  const laneCounts = days.map(() => 0)
  for (const { startCol, endCol, lane } of packed) {
    for (let col = startCol; col <= endCol; col++) {
      laneCounts[col] = Math.max(laneCounts[col], lane + 1)
    }
  }

  const rows = laneCounts.map((count, col) =>
    visibleRows(count, otherByDay[col]),
  )
  const slotsByDay = laneCounts.map((count, col) => Math.min(count, rows[col]))
  const hiddenByDay = days.map(() => 0)
  const segments: BarSegment[] = []

  for (const seg of packed) {
    let runStart: number | null = null
    const closeRun = (runEnd: number) => {
      if (runStart === null) return
      segments.push({
        ...seg,
        startCol: runStart,
        endCol: runEnd,
        continuesBefore: seg.continuesBefore || runStart > seg.startCol,
        continuesAfter: seg.continuesAfter || runEnd < seg.endCol,
      })
      runStart = null
    }
    for (let col = seg.startCol; col <= seg.endCol; col++) {
      if (seg.lane < rows[col]) {
        if (runStart === null) runStart = col
      } else {
        hiddenByDay[col]++
        closeRun(col - 1)
      }
    }
    closeRun(seg.endCol)
  }

  return { segments, rowsByDay: rows, slotsByDay, hiddenByDay }
}
